import { differenceInDays, differenceInMonths, parse } from 'date-fns';
import { f7 } from 'framework7-react';
import { FC } from 'react';
import { CarChangeList } from '../CarChangeList/CarChangeList';
import style from './carInfoCardStyle.module.css';
import aiAvatar from '@/assets/images/ai-avatar.png';
import carImg from '@/assets/images/carImg.svg';
import { TenantSwitchBanner } from '@/components/projects/TenantSwitchBanner/TenantSwitchBanner';
import { Button } from '@/components/uiParts/Button';
import { isApp } from '@/config/device';
import { SelectedTenant } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { affiliateId } from '@/consts/shop';
import { Affiliate } from '@/types/api/authApi';
import { Car } from '@/types/api/customerApi';
import { generateTransportName, isDelivered } from '@/utils/car';
import {
  dateToSlashConnection,
  getCurrentTimeInTokyo,
  removeTimeFromDate,
} from '@/utils/date';
import { carNameTitle } from '@/utils/home';
import { getCarPhoto } from '@/utils/utils';

interface CarInfoCardProps {
  selectedCar?: Car;
  isOpenCarList: boolean;
  isBasicInfoLoaded: boolean;
  affiliatesInfo: Affiliate[];
  isShowSwitchCar: boolean;
  isShowCarAssetValue: boolean;
  warrantyStatusText?: string;
  notificationCount?: number;
  selectedTenant?: SelectedTenant;
  handleClickRegistCar: () => void;
  handleClickListImage: (
    car: Affiliate['cars'][0] | null,
    affiliateID: number,
  ) => Promise<void>;
  changeIsOpenCarList: (bool: boolean) => void;
}
export const CarInfoCard: FC<CarInfoCardProps> = ({
  selectedCar,
  isOpenCarList,
  isBasicInfoLoaded,
  affiliatesInfo,
  isShowSwitchCar,
  isShowCarAssetValue,
  warrantyStatusText,
  notificationCount,
  selectedTenant,
  handleClickRegistCar,
  handleClickListImage,
  changeIsOpenCarList,
}) => {
  // 車両輸送名
  const transportName = generateTransportName(selectedCar);

  const carInfoCardTitle = [selectedCar?.maker_nm, selectedCar?.car_nm]
    .filter(Boolean)
    .join(' ');

  const deliveryConfirmedDate = (dateString: string) => {
    if (!dateString) return '';

    const date = new Date(dateString);

    return dateToSlashConnection(date);
  };

  const handleClickCarImage = () => {
    f7.view.main.router.navigate(
      paths.car(String(selectedCar?.m_car_id) || ''),
      {
        props: { isChangeCarImage: true },
      },
    );
  };

  const handleClickCarOrder = (id?: number) => {
    if (id == null) return;
    f7.view.main.router.navigate(paths.carOrder(id.toString()));
  };

  const monthsUntilInspection = selectedCar?.next_inspection_date
    ? differenceInMonths(
        new Date(selectedCar.next_inspection_date),
        removeTimeFromDate(getCurrentTimeInTokyo()),
      )
    : null;

  const inspectionDateFormatted = selectedCar?.next_inspection_date
    ? dateToSlashConnection(new Date(selectedCar.next_inspection_date))
    : '';

  const daysUntilDelivery = selectedCar?.delivery_confirmed_date
    ? differenceInDays(
        parse(
          selectedCar.delivery_confirmed_date,
          'yyyy-MM-dd',
          getCurrentTimeInTokyo(),
        ),
        removeTimeFromDate(getCurrentTimeInTokyo()),
      )
    : -1;

  const carTitle = carNameTitle(
    isBasicInfoLoaded,
    isDelivered(selectedCar?.delivery_complete_flg),
    carInfoCardTitle ? carInfoCardTitle : '現在契約中の車両はありません',
  );

  return (
    <>
      <CarChangeList
        isOpenCarList={isOpenCarList}
        selectedCar={selectedCar}
        affiliatesInfo={affiliatesInfo}
        changeIsOpenCarList={changeIsOpenCarList}
        handleClickListImage={handleClickListImage}
        handleClickRegistCar={handleClickRegistCar}
      />
      <div className={style['car-card-wrapper']}>
        <div className={style['car-card']}>
          {/* テナント切り替え中の表示（グラデーション上に重ねる） */}
          <TenantSwitchBanner selectedTenant={selectedTenant ?? null} />
          {/* ナビゲーションアイコン */}
          <div className={style['car-header-nav']}>
            <div
              className={style['ai-wrapper']}
              onClick={() => f7.view.main.router.navigate(paths.aiChat)}
              aria-label="マイくるAIに相談する"
            >
              <img src={aiAvatar} alt="" className={style['ai-avatar']} />
            </div>
            <div className={style['car-header-nav-right']}>
              <div
                className={style['bell-wrapper']}
                onClick={() => f7.view.main.router.navigate(paths.news)}
              >
                <i className={`icon-notification ${style['nav-icon']}`} />
                {!!notificationCount && (
                  <span className={style['bell-badge']}>
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </div>
              <div
                className={style['account-wrapper']}
                onClick={() => f7.view.main.router.navigate(paths.account)}
              >
                <i className={`icon-user ${style['nav-icon']}`} />
              </div>
            </div>
          </div>

          {/* 車両情報 */}
          <div className={style['car-selected-wrapper']}>
            <div
              className={style['car-image-icon']}
              onClick={handleClickCarImage}
            >
              <img src={getCarPhoto(selectedCar as Car, false) || carImg} />
              {!isDelivered(selectedCar?.delivery_complete_flg) && (
                <>
                  <div className={style['car-coating']}> </div>
                  <span className={style['car-label']}>納車準備中</span>
                </>
              )}
            </div>
            <div className={style['car-information-wrapper']}>
              <span className={style['car-information-title']}>{carTitle}</span>
              {carInfoCardTitle
                ? transportName && (
                    <span className={style['car-information-number']}>
                      {transportName}
                    </span>
                  )
                : isBasicInfoLoaded &&
                  isApp && (
                    <span className={style['car-information-number']}>
                      車検証を読み取ることで車両情報をアプリに登録できます。
                    </span>
                  )}
              {warrantyStatusText && (
                <span className={style['warranty-status']}>
                  {warrantyStatusText}
                </span>
              )}
            </div>
          </div>

          {/* 車検証読み取りボタン（未登録時） */}
          {isBasicInfoLoaded &&
            !carInfoCardTitle &&
            isApp &&
            (store.state.customerInfo.m_affiliate_id === affiliateId.YAMADA ||
              store.state.customerInfo.m_affiliate_id ===
                affiliateId.YAMADA_2 ||
              store.state.customerInfo.m_affiliate_id ===
                affiliateId.ATSUMI) && (
              <div className={style['car-card-button-group']}>
                <Button
                  className={style['car-regist-btn']}
                  onClick={handleClickRegistCar}
                >
                  車検証を読み取る
                </Button>
              </div>
            )}

          {/* アクションボタン */}
          <div className={style['car-card-button-group']}>
            {!!isShowSwitchCar && (
              <div className={style['car-card-button-wrapper']}>
                <a
                  className={style['car-card-button']}
                  onClick={() => changeIsOpenCarList(true)}
                >
                  <i className={`icon-reload-refresh ${style.icon}`} />
                  車両を切り替える
                </a>
              </div>
            )}
            {!!selectedCar?.t_order_id &&
              !isDelivered(selectedCar?.delivery_complete_flg) && (
                <div className={style['car-card-button-wrapper']}>
                  <a
                    className={style['car-card-button']}
                    onClick={() => handleClickCarOrder(selectedCar?.t_order_id)}
                  >
                    <i className={`icon-file-color-inside ${style.icon}`} />
                    注文書
                  </a>
                </div>
              )}
          </div>

          {/* 納車情報 */}
          {!isDelivered(selectedCar?.delivery_complete_flg) && (
            <div className={style['car-delivery']}>
              {daysUntilDelivery >= 0 && (
                <div className={style['car-delivery-count-down']}>
                  納車まであと<span>{daysUntilDelivery}日</span>
                </div>
              )}
              <div className={style['car-delivery-date']}>
                納車予定日：
                {deliveryConfirmedDate(
                  selectedCar?.delivery_confirmed_date || '',
                ) || '未定'}
              </div>
            </div>
          )}

          {/* 車検告知 */}
          {isDelivered(selectedCar?.delivery_complete_flg) &&
            monthsUntilInspection !== null &&
            monthsUntilInspection <= 6 && (
              <div className={style['car-inspection-notice']}>
                <div className={style['car-inspection-notice-info']}>
                  <div className={style['car-delivery-count-down']}>
                    車検満了日まで<span>{monthsUntilInspection}ヶ月</span>
                  </div>
                  <div className={style['car-delivery-date']}>
                    満了日：{inspectionDateFormatted}
                  </div>
                </div>
                {isShowCarAssetValue && (
                  <div
                    className={style['car-value-link']}
                    onClick={() =>
                      f7.view.main.router.navigate(paths.carAssetValue)
                    }
                  >
                    <img src={carImg} className={style['car-value-icon']} />
                    <div className={style['car-value-content']}>
                      <span className={style['car-value-badge']}>
                        乗り換えは今からでも間に合う！
                      </span>
                      <span className={style['car-value-text']}>
                        愛車の価値を確かめてみる？
                      </span>
                    </div>
                    <i className={`icon-right ${style['car-value-arrow']}`} />
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    </>
  );
};
